import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthenticationError } from './error/AuthenticationError';
import { GetUserManager } from './auth/AuthService';
import { useAppSelector } from './hooks/ReduxHooks';
import { useAuth } from './context/AuthContext';
import { getProfile } from './profile/actions';
import { getUserPersistedState } from './persistedstate/users/actions';
import { getOnboardingState } from './welcome/choosejourney/actions';
import { useDispatch } from 'react-redux';
import RootV2 from './RootV2';
import Loader from './components/loader';
import { getClientPersistedState } from './persistedstate/clients/actions';

export type SplashPageProps = {
  showLoading?: boolean;
};

export function SplashPageV2(props: SplashPageProps) {
  const dispatch = useDispatch();
  const profileLoaded = useAppSelector<boolean>((state) => state.profile.loaded);
  const profileLoadingError = useAppSelector<boolean>((state) => state.profile.error);
  const { isClientAuthenticated, authenticationError, isPartialIuAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const iUProfileUrl = pathname.includes('iuprofile');

  useEffect(() => {
    if (isClientAuthenticated || isPartialIuAuthenticated) {
      dispatch(getProfile());
      dispatch(getUserPersistedState());
      dispatch(getClientPersistedState());
    }

    if (isClientAuthenticated) {
      dispatch(getOnboardingState());
    }
  }, [isClientAuthenticated, isPartialIuAuthenticated]);

  if (authenticationError) {
    return (
      <AuthenticationError
        message={authenticationError}
        onClick={async () => {
          await GetUserManager().signinRedirect();
        }}
      />
    );
  }

  // Check first for any profile errors
  if (profileLoadingError) {
    return <AuthenticationError />;
  }

  // show loader if still loading
  if (!profileLoaded) {
    return <Loader isLoading={props.showLoading} />;
  }

  // if the user is an Independent User, and not client authenticated
  // always take them to their IU profile
  if (isPartialIuAuthenticated && !iUProfileUrl) {
    navigate('/iuprofile');
    return null;
  }

  // handle scenario where client authenticated user hits IUProfile page
  // redirect to home or forms page
  if (iUProfileUrl && isClientAuthenticated) {
    navigate('/');
    return null;
  }

  // if none of the above are true, load web app with profile
  return <RootV2 />;
}
